// The top bar's shift status (spec/13 §2): whether a shift is open at this venue, and since when. It
// reads the venue's open shift (F-OPS-01, `GET …/shifts/current`) — the same read the Shifts screen
// makes — and links there. A role that cannot run a shift (`shifts:own`) sees no status at all, and neither does a
// venue that does not track drawers (`shifts.enabled` off, ADR-0029).

import { useTranslation } from '@clavian/i18n/react';
import { cx } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAccess } from '../api/queries';
import { useBusinessDayStatus, useCurrentShift } from '../org/shifts/queries';
import type { MyVenue } from '../api/me';

const SHIFTS_OWN = 'shifts:own';

export function ShiftStatus({ venue }: { readonly venue: MyVenue }): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess({ venueId: venue.id });
  const canRead = access.status === 'ready' && access.can(SHIFTS_OWN);
  const shift = useCurrentShift(venue.organizationId, venue.id, canRead);
  const day = useBusinessDayStatus(venue.organizationId, venue.id, canRead);
  if (!canRead || day.data?.shiftsEnabled === false) return null;

  const current = shift.data ?? null;
  const open = current !== null;
  const since = current === null ? '' : clock(current.openedAt);
  return (
    <Link to="/v/$venueSlug/shifts" params={{ venueSlug: venue.slug }} className={cx('console-shift', open && 'console-shift--open')}>
      <span className="console-shift__dot" aria-hidden="true" />
      <span>{open ? t('shell.shiftOpen', { since }) : t('shell.shiftClosed')}</span>
    </Link>
  );
}

function clock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
